// Recruitment candidate management functionality
// 100% HTTP dependent - would use dedicated recruitment endpoints when available
// Currently returns empty data since recruitment endpoints are not implemented in this migration

import { get } from '@/api/lib/amplify-wrapper'
import { caseInsensitiveIncludes } from './string-utils.service'
import { createApiHeaders, logApiCall } from './api-utils'


// ============================================================================
// 🚀 RECRUITMENT TYPES AND INTERFACES
// ============================================================================

export interface RecruitmentCandidate {
  id: string;
  name: string;
  email: string;
  phone: string;
  position: string;
  source: 'linkedin' | 'headhunter' | 'referral' | 'website' | 'email';
  status: 'applied' | 'screening' | 'interview' | 'technical' | 'approved' | 'rejected';
  appliedDate: string;
  documents: {
    resume: boolean;
    portfolio: boolean;
    portfolioUrl?: string | null;
  };
  evaluation?: {
    score: number;
    skills: Array<{
      name: string;
      level: number;
    }>;
    notes: string;
    interviewer: string;
    interviewDate: string;
  };
  experienceYears: number;
  rawText?: string;
}

// ============================================================================
// 🚀 RECRUITMENT DATA
// ============================================================================

// Real recruitment candidates data extracted from extracted-candidates.json
export const ALL_RECRUITMENT_CANDIDATES: RecruitmentCandidate[] = [
  {
    id: 'ananda-dos-santos-de-oliveira',
    name: 'Ananda dos Santos de Oliveira',
    email: 'anandasbsb@gmail.com',
    phone: '(61) 98210-2231',
    position: 'Planejador de Marketing',
    source: 'headhunter',
    status: 'screening',
    appliedDate: '2025-07-19',
    documents: {
      resume: true,
      portfolio: true,
      portfolioUrl: 'https://linkedin.com/in/anandaolv/'
    },
    experienceYears: 1,
    evaluation: {
      score: 87, // Converted from 8.7 to integer
      skills: [
        { name: 'Photoshop', level: 9 },
        { name: 'Illustrator', level: 9 },
        { name: 'InDesign', level: 9 },
        { name: 'After Effects', level: 9 },
        { name: 'Premiere', level: 9 }
      ],
      notes: 'Candidata com sólida formação em Publicidade e Propaganda, experiência em design e planejamento estratégico. Destaque para projetos com grandes marcas como Oncoclínicas.',
      interviewer: 'Sistema Automatizado',
      interviewDate: '2025-08-07'
    },
    rawText: 'Ananda dos Santos de Oliveira\nPlanejamento e Estratégia\n(61) 98210-2231\n\nBrasília - DF\n\nanandasbsb@gmail.com\n\nlinkedin.com/in/anandaolv/\n\nFORMAÇÃO ACADÊMICA\n\nEXPERIÊNCIA PROFISSIONAL\n\nBacharelado em Publicidade e Propaganda\n\nGim Estratégias Digitais\n\nUniCEUB - Centro Universitário de Brasília\n| Previsão de conclusão em 1/2025\n\njulho 2024 - o momento\n\nEspecialista de Design\n\nCurso Superior em Marketing Digital\nUniCEUB - Centro Universitário de Brasília\n| Finalizado em 1/2023\n\nBacharelado em Comunicação Organizacional\nUnB - Universidade de Brasília\n| Cursado até o 4° Semestre\n\nago 2023 - julho 2024\n\nAnalista de Design\nCriação de peças gráficas para mídias sociais e materiais off-line\ncomo kit de marca e produtos.\nA frente de projetos como Qualifica Alimentos, Oncoclínicas e\nGrupo 3 S/A\n\nCURSOS\nCOMPLEMENTARES\n03/2025 Estrategista de Coração, Agora de Profissão\nMiami Ad School\n05/2022 Insight\nClube Share\n01/2024 Branding Strategy\nSandBox\n04/2024 Planejamento para Lideranças de Marketing\nRD'
  },
  {
    id: 'felipe-andrade-ottengy',
    name: 'Felipe Andrade Ottengy',
    email: 'felipe.ottengy@gmail.com',
    phone: '(73) 99859-3706',
    position: 'Planejador de Marketing',
    source: 'headhunter',
    status: 'screening',
    appliedDate: '2025-07-25',
    documents: {
      resume: true,
      portfolio: true,
      portfolioUrl: 'https://linkedin.com/in/felipe-ottengy'
    },
    experienceYears: 1,
    evaluation: {
      score: 87, // Converted from 8.7 to integer
      skills: [
        { name: 'Photoshop', level: 9 },
        { name: 'Illustrator', level: 9 },
        { name: 'InDesign', level: 9 },
        { name: 'Premiere', level: 9 },
        { name: 'Figma', level: 9 }
      ],
      notes: 'Designer experiente com mais de 10 anos de carreira. Forte background em UX/UI e projetos gráficos para diversos setores. Portfolio diversificado com destaque para Festival Internacional da Língua Portuguesa.',
      interviewer: 'Sistema Automatizado',
      interviewDate: '2025-08-07'
    },
    rawText: '📞\n🔗\n\n✉️\n\nFELIPE ANDRADE OTTENGY​\n(73) 99859-3706 |\nfelipe.ottengy@gmail.com​\nlinkedin.com/in/felipe-ottengy |\nbehance.net/felipe_ottengy\n\n🎨\n\nOBJETIVO\nDesigner Gráfico | UX/UI Designer | Product Designer\n\nRESUMO PROFISSIONAL\nDesigner com mais de 10 anos de experiência em projetos gráficos e digitais, atuando em\nsetores como saúde, tecnologia, varejo, inteligência artificial e educação. Ao longo da\ncarreira, desenvolveu desde identidades visuais e materiais editoriais até interfaces digitais\ncentradas no usuário.\nDestaques incluem a criação de layouts para livros, a liderança de equipe editorial em uma\nrevista e o desenvolvimento de cartazes para o Festival Internacional da Língua\nPortuguesa. Como UX/UI Designer, combina sensibilidade visual, narrativa estratégica e\ncolaboração em equipe para entregar experiências digitais intuitivas, acessíveis e\nvisualmente consistentes.\nEspecialista em prototipação, testes de usabilidade, design responsivo e aplicação de\nsistemas de design em ambie'
  },
  {
    id: 'aretuza-cerutti',
    name: 'Aretuza Cerutti',
    email: 'are.cerutti@gmail.com',
    phone: '(61) 98110-6091',
    position: 'Planejador de Marketing',
    source: 'headhunter',
    status: 'screening',
    appliedDate: '2025-07-15',
    documents: {
      resume: true,
      portfolio: false,
      portfolioUrl: null
    },
    experienceYears: 1,
    evaluation: {
      score: 85, // Converted from 8.5 to integer
      skills: [
        { name: 'Photoshop', level: 7 },
        { name: 'Illustrator', level: 7 },
        { name: 'InDesign', level: 7 },
        { name: 'Canva', level: 7 },
        { name: 'Figma', level: 7 }
      ],
      notes: 'Profissional com formação em UI Design para Web e Mobile. Demonstra evolução estratégica interessante, migrando do design para o planejamento. Boa visão de marketing digital e IA.',
      interviewer: 'Sistema Automatizado',
      interviewDate: '2025-08-07'
    },
    rawText: 'ARETUZA\nCERUTTI\n\n(61) 9 8110-6091\n\nBrasília - DF\n\nare.cerutti@gmail.com\n\nDesigner Gráfico\n\nSOBRE MIM\nOlá, meu nome é Aretuza, mas pode me chamar de Arê!\n\nMinha jornada no marketing digital começou pelo design, com formação em UI\nDesign para Web e Mobile. Depois disso, decidi expandir meu olhar e mergulhei em\nestudos sobre posicionamento de marca, marketing digital e inteligência artificial —\náreas que me fascinam e se conectam profundamente.\n\nNos últimos tempos, estive mais envolvida na parte estratégica justamente para\nentender melhor o fluxo completo do marketing. Essa vivência ampliou minha visão e\nfortaleceu ainda mais minha paixão pelo design. Agora, com essa bagagem mais\ncompleta, estou em busca de novas oportunidades como designer, pronta para\naplicar esse olhar estratégico em projetos criativos e impactantes.\n\nEstou sempre em busca de desafios que me façam crescer e evoluir. Acredito que\nesse movimento constante é o que me permite entregar o melhor em tudo que faço!\n\nEXPERIÊNCI'
  },
  {
    id: 'valdir-martins-j',
    name: 'Valdir Martins Júnior',
    email: 'valdir.martins.junior@gmail.com',
    phone: '(61) 99140-5232',
    position: 'Planejador de Marketing',
    source: 'headhunter',
    status: 'screening',
    appliedDate: '2025-07-21',
    documents: {
      resume: true,
      portfolio: true,
      portfolioUrl: 'https://linkedin.com/in/junior-martins-68b580169'
    },
    experienceYears: 2,
    evaluation: {
      score: 80, // Converted from 8.0 to integer
      skills: [
        { name: 'Photoshop', level: 6 },
        { name: 'Illustrator', level: 6 },
        { name: 'InDesign', level: 6 },
        { name: 'Adobe Creative Suite', level: 6 },
        { name: 'Marketing', level: 6 }
      ],
      notes: 'Profissional sênior com carreira iniciada em 2007. Ampla experiência em direção de arte e grandes projetos como Olimpíada do Conhecimento e WorldSkills. Atuação diversificada entre agências e freelances.',
      interviewer: 'Sistema Automatizado',
      interviewDate: '2025-08-07'
    },
    rawText: 'VALDIR MARTINS JÚNIOR\n37 anos\nCasado\nContato: 61 9 9140.5232​\nLinkedIn: linkedin.com/in/júnior-martins-68b580169\nBehance: behance.net/juniorcriad6e6\nFORMAÇÃO ACADÊMICA\nDesign Gráfico​\nInstituto de Educação Superior de Brasília (IESB) - Cursando\n\nEXPERIÊNCIA PROFISSIONAL:\nCursando Design Gráfico no IESB, a carreira na área foi iniciada em 2007 na\nagência Extrema Comunicação como arte-finalista, em que atuou também\ncomo Diretor de Arte. Em seguida, foi para a Atitude Comunicação e Marketing,\nna qual desenvolveu mais de 3 anos a atividade de Diagramação e Direção de\nArte. Em 2015, na Lowfat Comunicação, como Diretor de Arte e Arte-Finalista,\ndesenvolveu grandes projetos, tais como a Olimpíada do Conhecimento e o\nWorldSkills,Torneio Internacional de Educação Profissional. Paralelamente,\natuou em projetos freelancer pela Duo Design, Instituto Brasileiro de\nDesenvolvimento (IBDES), agência iComunicação Integrada, colégio Marista\nde Brasília, Duas Estúdio Criativo e Edi'
  },
  {
    id: 'gustavo-barboza',
    name: 'Gustavo Barboza',
    email: 'gustabarboza37@gmail.com',
    phone: '(61) 99999-9999',
    position: 'Planejador de Marketing',
    source: 'headhunter',
    status: 'screening',
    appliedDate: '2025-07-13',
    documents: {
      resume: true,
      portfolio: false,
      portfolioUrl: null
    },
    experienceYears: 2,
    evaluation: {
      score: 78, // Converted from 7.8 to integer
      skills: [
        { name: 'Photoshop', level: 9 },
        { name: 'Illustrator', level: 9 },
        { name: 'Adobe Creative Suite', level: 9 },
        { name: 'Marketing', level: 9 },
        { name: 'Design', level: 9 }
      ],
      notes: 'Designer versátil com experiência em vídeo, transmissões ao vivo e UX. Background sólido em marketing digital e SEO. Trabalhou na TriK Digital com foco em estratégia e execução criativa.',
      interviewer: 'Sistema Automatizado',
      interviewDate: '2025-08-07'
    },
    rawText: 'HISTÓRICO PROFISSIONAL\nDesign, Editor de Vídeo e SEO\nTriK Digital - (jan de 2024 - fev de 2025 · 1 a 2 m)\n\nGUSTAVO\nBARBOZA\nDESIGNER\nVIDEOMAKER\nMARKETING\nSEO\nTRANSMISSION SPECIALIST\n\nTrabalhei com Design, edição de vídeo, transmissões ao vivo e UX.\nMinha atuação envolveu do mais simples ao mais avançado,\nsempre com foco em garantir o melhor para os clientes.\nDesenvolvi apresentações para mostrar insights e melhorias,\nsempre focando em tornar o conteúdo mais eficiente e bem\nposicionado para os clientes. Com essa experiência, desenvolvi\nhabilidades para entregar soluções criativas e eficientes,\nalinhando estratégia e execução para atingir os melhores\nresultados.\n\nDesign e Editor de Vídeo\nTower Digital - (abr de 2021 - set de 2021 · 6 meses)\nProduzia o conteúdo de Marketing Digital para o Instagram, sites,\nYoutube, etc. Editei videos para o Youtube e cursos com\nthumbnails, intros, e finalizações. Para o Instagram, crio a\nfinalização e capa.\n\nDesigner\nViewNow - (jun de 2021 - jul de 2021 · '
  }
];

// ============================================================================
// 🚀 RECRUITMENT QUERY FUNCTIONS
// ============================================================================

export const getAllRecruitmentCandidates = (): RecruitmentCandidate[] => {
  return ALL_RECRUITMENT_CANDIDATES;
};

export const getCandidatesByStatus = (status: RecruitmentCandidate['status']): RecruitmentCandidate[] => {
  return ALL_RECRUITMENT_CANDIDATES.filter(candidate => candidate.status === status);
};

export const getCandidateById = (id: string): RecruitmentCandidate | null => {
  return ALL_RECRUITMENT_CANDIDATES.find(candidate => candidate.id === id) || null;
};

export const searchCandidates = (searchTerm: string): RecruitmentCandidate[] => {
  return ALL_RECRUITMENT_CANDIDATES.filter(candidate =>
    caseInsensitiveIncludes(candidate.name, searchTerm) ||
    caseInsensitiveIncludes(candidate.position, searchTerm) ||
    caseInsensitiveIncludes(candidate.email, searchTerm)
  );
};

export const getCandidatesBySource = (source: RecruitmentCandidate['source']): RecruitmentCandidate[] => {
  return ALL_RECRUITMENT_CANDIDATES.filter(candidate => candidate.source === source);
};

export const getCandidatesByPosition = (position: string): RecruitmentCandidate[] => {
  return ALL_RECRUITMENT_CANDIDATES.filter(candidate => 
    caseInsensitiveIncludes(candidate.position, position)
  );
};

export const getCandidatesByExperienceRange = (minYears: number, maxYears: number): RecruitmentCandidate[] => {
  return ALL_RECRUITMENT_CANDIDATES.filter(candidate => 
    candidate.experienceYears >= minYears && candidate.experienceYears <= maxYears
  );
};

// ============================================================================
// 🚀 RECRUITMENT ANALYTICS FUNCTIONS
// ============================================================================

export interface RecruitmentStats {
  total: number;
  byStatus: Record<RecruitmentCandidate['status'], number>;
  bySource: Record<RecruitmentCandidate['source'], number>;
  byPosition: Record<string, number>;
  conversionRate: number;
  rejectionRate: number;
  activeProcesses: number;
}

export const getRecruitmentStats = (): RecruitmentStats => {
  const total = ALL_RECRUITMENT_CANDIDATES.length;
  const byStatus = {
    applied: getCandidatesByStatus('applied').length,
    screening: getCandidatesByStatus('screening').length,
    interview: getCandidatesByStatus('interview').length,
    technical: getCandidatesByStatus('technical').length,
    approved: getCandidatesByStatus('approved').length,
    rejected: getCandidatesByStatus('rejected').length
  };
  
  const bySource = {
    linkedin: getCandidatesBySource('linkedin').length,
    headhunter: getCandidatesBySource('headhunter').length,
    referral: getCandidatesBySource('referral').length,
    website: getCandidatesBySource('website').length,
    email: getCandidatesBySource('email').length
  };

  const byPosition = ALL_RECRUITMENT_CANDIDATES.reduce((acc, candidate) => {
    acc[candidate.position] = (acc[candidate.position] || 0) + 1;
    return acc;
  }, {} as Record<string, number>);
  
  return {
    total,
    byStatus,
    bySource,
    byPosition,
    conversionRate: Math.round((byStatus.approved / total) * 100),
    rejectionRate: Math.round((byStatus.rejected / total) * 100),
    activeProcesses: total - byStatus.approved - byStatus.rejected
  };
};

export const getTopCandidates = (limit: number = 5): RecruitmentCandidate[] => {
  return ALL_RECRUITMENT_CANDIDATES
    .filter(candidate => candidate.evaluation)
    .sort((a, b) => (b.evaluation?.score || 0) - (a.evaluation?.score || 0))
    .slice(0, limit);
};

export const updateCandidateStatus = (
  candidateId: string, 
  newStatus: RecruitmentCandidate['status']
): boolean => {
  const candidateIndex = ALL_RECRUITMENT_CANDIDATES.findIndex(c => c.id === candidateId);
  if (candidateIndex === -1) return false;
  
  ALL_RECRUITMENT_CANDIDATES[candidateIndex].status = newStatus;
  return true;
};

export const getCandidatesNeedingAction = (): RecruitmentCandidate[] => {
  const now = new Date();
  const sevenDaysAgo = new Date(now.getTime() - (7 * 24 * 60 * 60 * 1000));
  
  return ALL_RECRUITMENT_CANDIDATES.filter(candidate => {
    const appliedDate = new Date(candidate.appliedDate);
    
    // Candidates that have been waiting too long in certain statuses
    if (candidate.status === 'applied' && appliedDate < sevenDaysAgo) return true;
    if (candidate.status === 'screening' && appliedDate < sevenDaysAgo) return true;
    
    // Candidates with evaluations but no final decision
    if (candidate.evaluation && (candidate.status === 'interview' || candidate.status === 'technical')) {
      const interviewDate = new Date(candidate.evaluation.interviewDate);
      const threeDaysAgo = new Date(now.getTime() - (3 * 24 * 60 * 60 * 1000));
      return interviewDate < threeDaysAgo;
    }
    
    return false;
  });
};

// ============================================================================
// 🚀 RECRUITMENT INSIGHTS FUNCTIONS
// ============================================================================

export const getRecruitmentInsights = (): any => {
  const stats = getRecruitmentStats();
  const topCandidates = getTopCandidates();
  const needingAction = getCandidatesNeedingAction();
  
  // Calculate average evaluation score
  const evaluatedCandidates = ALL_RECRUITMENT_CANDIDATES.filter(c => c.evaluation);
  const avgScore = evaluatedCandidates.length > 0 ? 
    evaluatedCandidates.reduce((sum, c) => sum + (c.evaluation?.score || 0), 0) / evaluatedCandidates.length : 0;
  
  // Most common positions
  const positionCounts = ALL_RECRUITMENT_CANDIDATES.reduce((acc, candidate) => {
    acc[candidate.position] = (acc[candidate.position] || 0) + 1;
    return acc;
  }, {} as Record<string, number>);
  
  const topPositions = Object.entries(positionCounts)
    .sort(([,a], [,b]) => b - a)
    .slice(0, 5)
    .map(([position, count]) => ({ position, count }));
  
  // Source effectiveness
  const sourceEffectiveness = Object.entries(stats.bySource).map(([source, count]) => {
    const sourceApproved = getCandidatesBySource(source as RecruitmentCandidate['source'])
      .filter(c => c.status === 'approved').length;
    const effectivenessRate = count > 0 ? Math.round((sourceApproved / count) * 100) : 0;
    
    return {
      source,
      candidates: count,
      approved: sourceApproved,
      effectivenessRate
    };
  }).sort((a, b) => b.effectivenessRate - a.effectivenessRate);
  
  return {
    overview: {
      totalCandidates: stats.total,
      activeProcesses: stats.activeProcesses,
      conversionRate: stats.conversionRate,
      avgEvaluationScore: Math.round(avgScore)
    },
    pipeline: stats.byStatus,
    topCandidates,
    needingAction: needingAction.length,
    candidatesNeedingAction: needingAction,
    topPositions,
    sourceEffectiveness,
    recommendations: generateRecruitmentRecommendations(stats, needingAction, sourceEffectiveness)
  };
};

// Generate recruitment recommendations
const generateRecruitmentRecommendations = (
  stats: any,
  needingAction: RecruitmentCandidate[],
  sourceEffectiveness: any[]
): string[] => {
  const recommendations: string[] = [];
  
  if (needingAction.length > 0) {
    recommendations.push(`${needingAction.length} candidato(s) precisam de ação imediata - processos parados há mais de 3-7 dias`);
  }
  
  if (stats.conversionRate < 15) {
    recommendations.push(`Taxa de conversão baixa (${stats.conversionRate}%) - revisar critérios de seleção ou processo`);
  }
  
  if (stats.activeProcesses > 10) {
    recommendations.push(`${stats.activeProcesses} processos ativos - considerar otimizar pipeline para reduzir gargalos`);
  }
  
  // Source recommendations
  const bestSource = sourceEffectiveness[0];
  const worstSource = sourceEffectiveness[sourceEffectiveness.length - 1];
  
  if (bestSource && worstSource && bestSource.effectivenessRate > worstSource.effectivenessRate * 2) {
    recommendations.push(`${bestSource.source} tem efetividade muito superior (${bestSource.effectivenessRate}%) - aumentar investimento nesta fonte`);
  }
  
  // Pipeline balance
  const pipelineBalance = stats.byStatus;
  if (pipelineBalance.interview > pipelineBalance.screening * 2) {
    recommendations.push('Muitos candidatos acumulados na fase de entrevista - acelerar processo de decisão');
  }
  
  if (recommendations.length === 0) {
    recommendations.push('Pipeline de recrutamento está equilibrado - continue monitorando indicadores');
  }
  
  return recommendations;
};

// ============================================================================
// 🚀 RECRUITMENT MANAGEMENT FUNCTIONS
// ============================================================================

// Add evaluation to candidate
export const addCandidateEvaluation = (
  candidateId: string,
  evaluation: RecruitmentCandidate['evaluation']
): boolean => {
  const candidateIndex = ALL_RECRUITMENT_CANDIDATES.findIndex(c => c.id === candidateId);
  if (candidateIndex === -1) return false;
  
  ALL_RECRUITMENT_CANDIDATES[candidateIndex].evaluation = evaluation;
  return true;
};

// Get candidates by skill
export const getCandidatesBySkill = (skillName: string, minLevel: number = 1): RecruitmentCandidate[] => {
  return ALL_RECRUITMENT_CANDIDATES.filter(candidate => {
    if (!candidate.evaluation?.skills) return false;
    
    const skill = candidate.evaluation.skills.find(s => 
      caseInsensitiveIncludes(s.name, skillName)
    );
    
    return skill && skill.level >= minLevel;
  });
};

// Get upcoming interviews
export const getUpcomingInterviews = (): RecruitmentCandidate[] => {
  const now = new Date();
  const nextWeek = new Date(now.getTime() + (7 * 24 * 60 * 60 * 1000));
  
  return ALL_RECRUITMENT_CANDIDATES.filter(candidate => {
    if (!candidate.evaluation?.interviewDate) return false;
    
    const interviewDate = new Date(candidate.evaluation.interviewDate);
    return interviewDate >= now && interviewDate <= nextWeek;
  }).sort((a, b) => {
    const dateA = new Date(a.evaluation!.interviewDate);
    const dateB = new Date(b.evaluation!.interviewDate);
    return dateA.getTime() - dateB.getTime();
  });
};

// Export recruitment summary for reporting
export const getRecruitmentSummaryReport = (): any => {
  const insights = getRecruitmentInsights();
  const stats = getRecruitmentStats();
  
  return {
    reportDate: new Date().toISOString(),
    summary: insights.overview,
    pipeline: insights.pipeline,
    sourceAnalysis: insights.sourceEffectiveness,
    topPositions: insights.topPositions,
    actionItems: {
      needingAction: insights.needingAction,
      upcomingInterviews: getUpcomingInterviews().length,
      topCandidates: insights.topCandidates.map((c: RecruitmentCandidate) => ({
        name: c.name,
        position: c.position,
        score: c.evaluation?.score
      }))
    },
    recommendations: insights.recommendations
  };
};